import type { CodeDecoder, ScanMode } from "./scan";
import type { ContainerAddPresetProps } from "./ContainerAddPreset";
import type { IdentifyPresetProps } from "./IdentifyPreset";
import type { ItemCreatePresetProps } from "./ItemCreatePreset";
import { ContainerAddPreset } from "./ContainerAddPreset";
import { IdentifyPreset } from "./IdentifyPreset";
import { ItemCreatePreset } from "./ItemCreatePreset";
import { GlobalPreset } from "./GlobalPreset";

interface GlobalPresetVariantProps {
  variant: "global";
  decoder?: CodeDecoder;
  mode?: ScanMode;
}

interface ContainerAddVariantProps extends ContainerAddPresetProps {
  variant: "container-add";
}

interface ItemCreateVariantProps extends ItemCreatePresetProps {
  variant: "item-create";
}

interface IdentifyVariantProps extends IdentifyPresetProps {
  variant: "identify";
}

export type ScannerSurfaceProps =
  | GlobalPresetVariantProps
  | ContainerAddVariantProps
  | ItemCreateVariantProps
  | IdentifyVariantProps;

/**
 * The one camera surface: a named preset per entry point (scanner-surface 01,
 * 02, 03, 05). `global` owns the `/scan` page; `container-add` and `item-create`
 * own their dialogs' camera, lookup and write flows; `identify` resolves a
 * label for a caller (the container-detail link flow) and writes nothing. No
 * dialog carries camera markup of its own.
 *
 * This component is only the dispatcher: each preset owns its own state and
 * render tree, so a change to one flow cannot ripple through the others.
 */
export function ScannerSurface(props: ScannerSurfaceProps) {
  switch (props.variant) {
    case "container-add":
      return (
        <ContainerAddPreset
          containerId={props.containerId}
          onItemAdded={props.onItemAdded}
          onClose={props.onClose}
          decoder={props.decoder}
        />
      );
    case "item-create":
      return (
        <ItemCreatePreset
          onCreated={props.onCreated}
          onClose={props.onClose}
          initialBarcode={props.initialBarcode}
          decoder={props.decoder}
        />
      );
    case "identify":
      return (
        <IdentifyPreset
          onResolved={props.onResolved}
          onClose={props.onClose}
          hint={props.hint}
          resolveLabel={props.resolveLabel}
          errorMessage={props.errorMessage}
          decoder={props.decoder}
        />
      );
    case "global":
      return <GlobalPreset decoder={props.decoder} mode={props.mode} />;
  }
}
